import React, { useEffect, useRef, useState } from 'react';
import type { PublicLogEntry } from '#shared';
import { soundEngine } from '../audio/soundEngine.ts';
import { CardSleeveBack } from './CardVisual.tsx';

interface FlyingGhost {
  id: string;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  cardImageUrl: string | null;
  cardName: string | null;
  active: boolean;
}

export function FlightLayer({
  recentLog,
  reducedMotion,
}: {
  recentLog: PublicLogEntry[];
  reducedMotion: boolean;
}) {
  const [ghosts, setGhosts] = useState<FlyingGhost[]>([]);
  const lastProcessedSeqRef = useRef<number | null>(null);

  useEffect(() => {
    if (recentLog.length === 0) return;
    const newest = recentLog[recentLog.length - 1];
    if (lastProcessedSeqRef.current === null) {
      lastProcessedSeqRef.current = newest.seq;
      return;
    }
    if (newest.seq <= lastProcessedSeqRef.current) {
      lastProcessedSeqRef.current = newest.seq;
      return;
    }

    const freshEntries = recentLog.filter((e) => e.seq > (lastProcessedSeqRef.current ?? 0));
    lastProcessedSeqRef.current = newest.seq;

    for (const entry of freshEntries) {
      const anim = entry.animation;
      if (!anim) continue;

      if (anim.kind === 'shuffle') {
        soundEngine.play('shuffleRiffle');
        continue;
      }
      if (anim.kind === 'tap') {
        soundEngine.play('tapClick');
        continue;
      }
      if (anim.kind === 'flag') {
        soundEngine.play('flagChime');
        continue;
      }
      if (anim.kind === 'card_move') {
        if (anim.toZone === 'hand') {
          soundEngine.play('drawSlide');
        } else if (anim.toZone === 'battlefield') {
          soundEngine.play('playFlick');
          setTimeout(() => soundEngine.play('tableThud'), 85);
        } else {
          soundEngine.play('playFlick');
        }

        if (reducedMotion || !anim.fromSeatId || !anim.toSeatId || !anim.fromZone || !anim.toZone) {
          continue;
        }

        const fromEl = document.querySelector(`[data-zone-anchor="${anim.fromSeatId}:${anim.fromZone}"]`);
        const toEl = document.querySelector(`[data-zone-anchor="${anim.toSeatId}:${anim.toZone}"]`);
        if (!fromEl || !toEl) continue;

        const r1 = fromEl.getBoundingClientRect();
        const r2 = toEl.getBoundingClientRect();
        const id = `fly_${entry.seq}_${Math.random().toString(36).slice(2, 6)}`;

        const ghost: FlyingGhost = {
          id,
          fromX: r1.left + r1.width / 2 - 35,
          fromY: r1.top + r1.height / 2 - 49,
          toX: r2.left + r2.width / 2 - 35,
          toY: r2.top + r2.height / 2 - 49,
          cardImageUrl: anim.cardImageUrl ?? null,
          cardName: anim.cardName ?? null,
          active: false,
        };

        setGhosts((prev) => [...prev, ghost]);
        requestAnimationFrame(() => {
          setGhosts((prev) => prev.map((g) => (g.id === id ? { ...g, active: true } : g)));
        });
        setTimeout(() => {
          setGhosts((prev) => prev.filter((g) => g.id !== id));
        }, 360);
      }
    }
  }, [recentLog, reducedMotion]);

  if (ghosts.length === 0) return null;

  return (
    <div aria-hidden="true" className="fixed inset-0 pointer-events-none z-[90] overflow-hidden">
      {ghosts.map((g) => (
        <div
          key={g.id}
          style={{
            transform: `translate3d(${g.active ? g.toX : g.fromX}px, ${g.active ? g.toY : g.fromY}px, 0) rotate(${
              g.active ? '0deg' : '-8deg'
            }) scale(${g.active ? 1 : 0.88})`,
            opacity: g.active ? 0.96 : 0.85,
          }}
          className="absolute top-0 left-0 transition-all duration-300 ease-out shadow-2xl"
        >
          {g.cardImageUrl ? (
            <img
              src={g.cardImageUrl}
              alt={g.cardName ?? ''}
              className="w-[70px] h-[98px] rounded-lg border border-amber-400/70 object-cover shadow-xl"
            />
          ) : (
            <CardSleeveBack size="sm" />
          )}
        </div>
      ))}
    </div>
  );
}
